import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  ApproveOnboardingStep,
  RejectOnboarding,
  RecollectOnboardingStep,
  UnrejectOnboarding,
  UpdateCollectData,
  DeclineOnboardingStep,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from '../api/callApi'
import { CollectDataStepDeclineReason } from 'src/constants/collectedData'

interface RejectOnboardingRequest {
  partnerDriverId: string
}

function* onRejectOnboarding(action: AsyncAction<RejectOnboardingRequest, any>) {
  const { partnerDriverId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.RejectOnboarding, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        partnerDriverId,
      },
    })

    yield put(RejectOnboarding.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to reject onboarding'

    yield put(RejectOnboarding.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UnrejectOnboardingRequest {
  partnerDriverId: string
}

function* onUnrejectOnboarding(action: AsyncAction<UnrejectOnboardingRequest, any>) {
  const { partnerDriverId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.UnrejectOnboarding, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        partnerDriverId,
      },
    })

    yield put(UnrejectOnboarding.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to unreject onboarding'

    yield put(UnrejectOnboarding.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ApproveOnboardingStepRequest {
  stepId: string
  partnerDriverId: string
  effectiveAt?: string
  expiresAt?: string
}

function* onApproveOnboardingStep(action: AsyncAction<ApproveOnboardingStepRequest, any>) {
  const { stepId, partnerDriverId, effectiveAt, expiresAt } = action.payload

  try {
    const body: {
      stepId: string
      partnerDriverId: string
      effectiveAt?: string
      expiresAt?: string
    } = {
      stepId,
      partnerDriverId,
    }

    if (effectiveAt != null) {
      body.effectiveAt = effectiveAt
    }

    if (expiresAt != null) {
      body.expiresAt = expiresAt
    }

    yield call(callSecureApi, Endpoints.ApproveOnboardingStep, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body,
    })

    yield put(ApproveOnboardingStep.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to recollect onboarding step'

    yield put(ApproveOnboardingStep.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface RecollectOnboardingStepRequest {
  stepId: string
  partnerDriverId: string
  reviewedByEmail: string
}

function* onRecollectOnboardingStep(action: AsyncAction<RecollectOnboardingStepRequest, any>) {
  const { stepId, partnerDriverId, reviewedByEmail } = action.payload

  try {
    yield call(callSecureApi, Endpoints.RecollectOnboardingStep, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        stepId,
        partnerDriverId,
        stepMetadata: {
          declineReason: CollectDataStepDeclineReason.DeclinedByCustomer,
          reviewedBy: reviewedByEmail,
        },
      },
    })

    yield put(RecollectOnboardingStep.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to decline onboarding step'

    yield put(RecollectOnboardingStep.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DeclineOnboardingStepRequest {
  stepId: string
  partnerDriverId: string
  reviewedByEmail: string
  declineReason?: string
  partnerReviewRejectionReasonId?: string
}

function* onDeclineOnboardingStep(action: AsyncAction<DeclineOnboardingStepRequest, any>) {
  const {
    stepId,
    partnerDriverId,
    reviewedByEmail,
    declineReason,
    partnerReviewRejectionReasonId,
  } = action.payload

  try {
    const body: {
      stepId: string
      partnerDriverId: string
      declineReason?: string
      stepMetadata: {
        declineReason: string
        reviewedBy: string
      }
      partnerReviewRejectionReasonId?: string
    } = {
      stepId,
      partnerDriverId,
      stepMetadata: {
        declineReason: declineReason || CollectDataStepDeclineReason.DeclinedByCustomer,
        reviewedBy: reviewedByEmail,
      },
    }

    if (declineReason) {
      body.declineReason = declineReason
    }

    if (partnerReviewRejectionReasonId) {
      body.partnerReviewRejectionReasonId = partnerReviewRejectionReasonId
    }

    yield call(callSecureApi, Endpoints.DeclineOnboardingStep, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body,
    })

    yield put(DeclineOnboardingStep.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to approve onboarding step'

    yield put(DeclineOnboardingStep.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateCollectDataRequest {
  stepId: string
  partnerDriverId: string
  expiresAt?: string
  effectiveAt?: string
}

function* onUpdateCollectData(action: AsyncAction<UpdateCollectDataRequest, any>) {
  const { stepId, partnerDriverId, expiresAt, effectiveAt } = action.payload

  try {
    const body: Record<string, string> = {}

    if (expiresAt != null) {
      body.expiresAt = expiresAt
    }

    if (effectiveAt != null) {
      body.effectiveAt = effectiveAt
    }

    yield call(callSecureApi, Endpoints.UpdateCollectData, {
      method: 'PUT',
      apiService: ApiService.Paraworks,
      params: [
        {
          field: ':stepId',
          value: stepId,
        },
        {
          field: ':partnerDriverId',
          value: partnerDriverId,
        },
      ],
      body,
    })

    yield put(UpdateCollectData.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update onboarding step'

    yield put(UpdateCollectData.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* onboardingActionsSaga() {
  yield takeEvery(RejectOnboarding.type.REQUEST, onRejectOnboarding)
  yield takeEvery(UnrejectOnboarding.type.REQUEST, onUnrejectOnboarding)
  yield takeEvery(ApproveOnboardingStep.type.REQUEST, onApproveOnboardingStep)
  yield takeEvery(RecollectOnboardingStep.type.REQUEST, onRecollectOnboardingStep)
  yield takeEvery(UpdateCollectData.type.REQUEST, onUpdateCollectData)
  yield takeEvery(DeclineOnboardingStep.type.REQUEST, onDeclineOnboardingStep)
}
